import { Icon } from '@mdvip/shared/components/blocks/Icon';
import { Button } from '@mdvip/shared/components/ui/button';
import { Card } from '@mdvip/shared/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@mdvip/shared/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@mdvip/shared/components/ui/sheet';
import { useIsMobile } from '@mdvip/shared/hooks/useIsMobile';
import type { ReactNode } from 'react';

interface ConfirmRemoveDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isPending: boolean;
  title: string;
  description: string;
  /** Icon + masked-value pair for the preview row; omit while the target item is null (e.g. mid-close). */
  preview?: ReactNode;
  cancelLabel: string;
  confirmLabel: string;
  confirmSubmittingLabel: string;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * Presentational shell for a destructive-removal confirmation — responsive `Dialog` (desktop)/
 * `Sheet` (mobile) swap, icon-circle header, and an optional masked-value preview row. Shared by
 * `RemoveCardDialog` and `RemoveBankAccountDialog`; each owns its own type-specific mutation hook
 * (different failure-code enums, different cache sub-field) and just supplies copy + preview here —
 * mirrors why `EditCreditCardModal`/`EditBankAccountModal` stay separate above their own shared
 * pieces rather than branching on account type inside one generalized component.
 */
export function ConfirmRemoveDialog({
  open,
  onOpenChange,
  isPending,
  title,
  description,
  preview,
  cancelLabel,
  confirmLabel,
  confirmSubmittingLabel,
  onCancel,
  onConfirm,
}: ConfirmRemoveDialogProps) {
  const isMobile = useIsMobile();

  const confirmContent = isPending ? (
    <>
      <Icon name="spinner" weight="solid" className="animate-spin" />
      {confirmSubmittingLabel}
    </>
  ) : (
    <>
      <Icon name="trash" />
      {confirmLabel}
    </>
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="bottom"
          showCloseButton={false}
          className="[padding-bottom:max(1.5rem,env(safe-area-inset-bottom))]"
        >
          <SheetHeader className="items-center text-center">
            <div className="mb-1 flex h-14 w-14 items-center justify-center rounded-full bg-destructive text-destructive-foreground">
              <Icon name="trash" className="text-2xl" />
            </div>
            <SheetTitle className="text-2xl font-semibold leading-tight text-foreground">{title}</SheetTitle>
            <SheetDescription className="mx-auto max-w-xs">{description}</SheetDescription>

            {preview && (
              <Card className="w-full flex-row! items-center gap-3! rounded-lg! border-border bg-muted px-3.5 py-3! text-left shadow-none!">
                {preview}
              </Card>
            )}
          </SheetHeader>

          <SheetFooter className="flex-col-reverse gap-2 px-6 sm:flex-col-reverse sm:justify-normal [&>button]:w-full">
            <Button variant="outline" onClick={onCancel} disabled={isPending}>
              {cancelLabel}
            </Button>
            <Button variant="destructive" disabled={isPending} onClick={onConfirm}>
              {confirmContent}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showCloseButton={false} className="sm:max-w-sm">
        <div className="flex flex-col items-center pb-1 text-center">
          <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-destructive text-destructive-foreground">
            <Icon name="trash" className="text-2xl" />
          </div>
          <DialogTitle className="text-2xl font-semibold leading-tight text-foreground">{title}</DialogTitle>
          <DialogDescription className="mx-auto mt-1.5 max-w-xs">{description}</DialogDescription>

          {preview && (
            <Card className="mt-4 w-full flex-row! items-center gap-3! rounded-lg! border-border bg-muted px-3.5 py-3! text-left shadow-none!">
              {preview}
            </Card>
          )}
        </div>

        <div className="flex flex-col-reverse gap-2 sm:flex-row [&>button]:flex-1">
          <Button variant="outline" onClick={onCancel} disabled={isPending}>
            {cancelLabel}
          </Button>
          <Button variant="destructive" disabled={isPending} onClick={onConfirm}>
            {confirmContent}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
